import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDividerModule } from '@angular/material/divider';
import { MatSliderModule } from '@angular/material/slider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Store } from "@ngrx/store";
import { TranslateModule } from '@ngx-translate/core';
import { cloneDeep } from "lodash-es";
import { seTimelineComponentSetData, seTimelineComponentSetFilters, seTimelineComponentSetReport, seTimelineComponentSetReportFilters } from "../redux/se-timeline.actions";
import { SeTimelineMonthsSliderComponent } from '../se-timeline-months-slider/se-timeline-months-slider.component';
import { Months, PastFutureInfo, TimelineFilters, VisitPosition, VisitPositionMap, YearMonthsData } from "../se-timeline.models";

@Component({
    selector: 'se-timeline-filters',
    templateUrl: './se-timeline-filters.component.html',
    styleUrls: ['./se-timeline-filters.component.scss'],
    standalone: true,
    imports: [CommonModule, MatSliderModule, MatDividerModule, MatTooltipModule, FormsModule, SeTimelineMonthsSliderComponent, TranslateModule],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SeTimelineFiltersComponent implements OnInit, OnChanges {

    @Input() data: YearMonthsData[] = [];
    @Input() mode: 'clinical' | 'report' = 'clinical';
    @Output() showInfo : EventEmitter<PastFutureInfo> = new EventEmitter<PastFutureInfo>();
    @Output() filtersChange : EventEmitter<TimelineFilters> = new EventEmitter<TimelineFilters>();

    clonedData : YearMonthsData[] = [];
    currentYear : string = '';
    endValue : number = 0;
    filteredHistory : YearMonthsData[] = [];
    filters : TimelineFilters = { min: null, max: null, isMonthlyView: false };
    isMonthlyView : boolean = false;
    maxValue : number = 0;
    maxValueAllowed : number = 14;
    minValue : number = 0;
    minValueAllowed : number = 0;
    months : Months[] | string[] = [];
    points : any = {};
    startValue : number = 0;
    screenWidth : number = window.innerWidth - 40;
    years : any[] = [{ date: '', description: '' }];
    yearWidth : number = 0;
    visitCounts : { [key : string] : number; } = {}; 
    constructor (
        private cdr : ChangeDetectorRef,
        private store : Store
    ) { };   

    @HostListener('window:resize', ['$event'])
    onResize (event : any) {
        this.screenWidth = event.target.innerWidth - 20;
        this.calculateYearWidth();
    }

    ngOnInit() {
        if (!!this.data) {
            this.initializeComponent();
        }
    }

    ngOnChanges (changes : SimpleChanges) {
        if (changes && changes.data) {
            this.initializeComponent();
        }
    }

    private initializeComponent () {
        this.clonedData = cloneDeep(this.data);
        this.updateYearsAndView(this.clonedData);
        this.initFilters();
        this.calculateStartEnd();
    }

    private initFilters () {
        if (!!this.years) {
            const minYear = this.data.length === 1 ? Number(this.years[0].date.split('-')[0]) : Number(this.years[0].date);
            const maxYear = Number(this.years[this.years.length - 1].date);

            this.filters = {
                min: new Date(minYear, 0),
                max: new Date(maxYear, 0),
                isMonthlyView: this.data.length === 1
            };
        } 
    }

    private updateYearsAndView (data : YearMonthsData[], min?: number, max?: number) {
        this.years = this.createYearRange(data);
        this.months = this.getMonthsArray(data);
        this.visitCounts = this.countVisitsPerMonth(data);
        this.points = this.calculatePointPositions(data);
        this.calculateYearWidth();
        this.cdr.detectChanges();
    }

    private calculateStartEnd () {
        if (this.filters.min && this.filters.max) {
            this.startValue = this.filters.min.getFullYear();
            if (this.filters.max.getMonth() !== 0 && !this.isMonthlyView) {
                this.filters.max = new Date(this.filters.max.getFullYear() + 1, 0);
            }
            this.endValue = this.filters.max.getFullYear();
        }
    }

    private calculateYearWidth () {
        this.yearWidth = this.screenWidth / this.years.length;
    }

    private calculatePointPositions (data : YearMonthsData[]) : VisitPositionMap {
        return data.reduce((result, { year, months }) => {
            const isSingleYear = data.length === 1;
            const yearWidth = isSingleYear ? this.screenWidth : this.screenWidth / this.years.length + 1;
            const totalMonths = 12;
            const monthWidth = yearWidth / totalMonths;
            const visitPositions : VisitPosition[] = [];

            months.forEach(monthData => {
                const month = monthData.month;
                const visitsInMonth = monthData.visits;
                const yearMonthKey = `${year}-${month.toString().padStart(2, '0')}`;
                const groupedVisits = visitsInMonth.map(({ date, description }) => ({ date, description }));
                const positionInMonth = monthWidth / 2;

                if (!result[yearMonthKey]) {
                    result[yearMonthKey] = [];
                }

                // visite singole (in 1 mese)
                if (visitsInMonth.length > 1) {
                    if (isSingleYear) {
                        let position = 20;
                        result[yearMonthKey].push({ grouped: groupedVisits, position, date: new Date(Number(year), month - 1) });
                    } else {
                        let position = ((month - 1) * monthWidth + positionInMonth + 10) - (yearWidth / 2);
                        visitPositions.push({ grouped: groupedVisits, position, date: new Date(Number(year), month - 1) });
                        result[year] = visitPositions;
                    }
                } else {
                    // visite multiple (in 1 mese)
                    visitsInMonth.forEach((visit, index) => {
                        if (isSingleYear) {
                            const visitDate = new Date(visit.date);
                            const dayOfMonth = visitDate.getDate();
                            const positionInMonth = (dayOfMonth < 15) ? monthWidth / 4 : (3 * monthWidth) / 4;
                            let position = (positionInMonth + 10) - (monthWidth / 2);
                            result[yearMonthKey].push({ description: visit.description, position, date: visit.date });
                        } else {
                            const positionInMonth = ((index * 20) + 10) * (index + 1);
                            let position = ((month - 1) * monthWidth + positionInMonth + 10) - (yearWidth / 2);
                            visitPositions.push({ description: visit.description, position, date: visit.date });
                            result[year] = visitPositions;
                        }
                    });
                }
            });
            return result;
        }, {});
    }

    private getMonthsArray (data : YearMonthsData[]) : Months[] {
        const monthNames = [
            'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
            'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
        ];

        if (data.length === 1) {
            this.currentYear = data[0].year;
            return Array.from({ length: 12 }, (_, i) => ({
                // creo una stringa nel formato aaaa-mm con 0 davanti al mese singolo
                date: `${this.currentYear}-${(i + 1).toString().padStart(2, '0')}`,
                description: monthNames[i]
            }));
        }
        return []; // ritorno array vuoto se data ha più di 1 anno
    }

    private countVisitsPerMonth (data : YearMonthsData[]) : { [key : string] : number; } {
        const visitCounts : { [key : string] : number; } = {};

        data.forEach(({ year, months }) => {
            months.forEach(monthData => {
                const month = monthData.month;
                const key = `${year}-${month.toString().padStart(2, '0')}`;
                visitCounts[key] = monthData.visits.length;
            });
        });
        return visitCounts;
    }

    private convertToMonths (value : number) {
        switch (value) {
            case 0:
                return new Date(this.minValue - 1, 0);
            case 1: case 2: case 3: case 4: case 5:
            case 6: case 7: case 8: case 9: case 10:
            case 11: case 12: case 13:
                return new Date(this.minValue, value - 1);
            case 14:
                return new Date(this.maxValue + 1, 0);
            default:
                return new Date();
        }
    }

    private checkIfMonthlyView (min : Date, max : Date) : boolean {
        let emptyYears = true;
        this.clonedData.forEach(year => {
            if (+year.year === min.getFullYear()) {
                year.months.forEach(month => {
                    emptyYears = !month.visits.some(visit => visit.date >= min && visit.date <= max);
                })
            }
        });
        if (emptyYears && !this.isMonthlyView) {
            return false;
        } else {
            const monthsDifference = this.getMonthsDifference(min, max);
            // Ritorna true se la differenza tra min e max è <= 12
            return monthsDifference <= 12;
        }
    }

    private getMonthsDifference (startDate : Date, endDate : Date) : number {
        const yearsDifference = endDate.getFullYear() - startDate.getFullYear();
        const monthsDifference = endDate.getMonth() - startDate.getMonth();
        return (yearsDifference * 12) + monthsDifference;
    }

    private isDifferenceMoreThanOneYear (startDate : Date, endDate : Date) : boolean {
        const startYear = startDate.getFullYear();
        const endYear = endDate.getFullYear();
        const startMonth = startDate.getMonth();
        const endMonth = endDate.getMonth();

        return startMonth !== 0 && endMonth == 0 && (endYear - 1) !== startYear ? true : false; 
    }

    private buildFilters () {
        if (this.isMonthlyView && this.isDifferenceMoreThanOneYear(this.filters.min!, this.filters.max!)) {
            // Resetto il mio valore minimo quando il massimo eccede la visualizzazione mensile (anno futuro)
            this.filters.min = new Date(this.minValue, 0);
        }

        let minYear = this.filters.min || new Date(this.minValue, 0);
        let maxYear = this.filters.max || new Date(this.maxValue, 0);
        this.isMonthlyView = this.checkIfMonthlyView(minYear, maxYear) || this.data.length === 1;
        let filter = {
            min: minYear,
            max: maxYear,
            isMonthlyView: this.isMonthlyView
        };

        this.onFilterApplied(filter);
        this.calculateStartEnd();
        this.emitVisitsToShow();
    }

    private emitVisitsToShow () {
        let value = {
            past: false,
            future: false
        };

        if (this.isMonthlyView) {
            outerLoop: for (const el of this.clonedData) {
                for (const month of el.months) {
                    for (const visit of month.visits) {
                        if (this.filters.min!.getTime() > visit.date.getTime()) {
                            value.past = true;
                        }
                        if (this.filters.max!.getTime() <= visit.date.getTime()) {
                            value.future = true;
                        }
                        // evito loop se tutte e due i flag sono già a true
                        if (value.past && value.future) {
                            break outerLoop;
                        }
                    }
                }
            }
        } else {
            value.past = this.filters.min!.getFullYear() > Number(this.clonedData[0].year);
            value.future = this.filters.max!.getFullYear() <= Number(this.clonedData[this.clonedData.length - 1].year);
        }

        this.showInfo.emit(value);
    }

    private buildFiltersValue () {
        this.buildFilters();
        if (this.isMonthlyView) {
            this.applyFiltersAndRecalculate();
        }
    }

    private buildFiltersValueByMonth () {
        this.buildFilters();
        if (this.clonedData.length > 1) {
            this.applyFiltersAndRecalculate();
        }
    }

    private filterMonthsView (filters : TimelineFilters) : YearMonthsData[] {
        const filterMonths = (years : YearMonthsData) => {
            const filteredMonths = years.months.filter(monthData => {
                const month = new Date(Number(years.year), monthData.month - 1);
                if (filters.min && filters.max) {
                    return month >= filters.min && month < filters.max;
                }
            });
            return { ...years, months: filteredMonths };
        };
        return this.clonedData.map(filterMonths).filter(years => years.months.length > 0);
    }

    private filterYearsView (filters : TimelineFilters) : YearMonthsData[] {
        return this.clonedData.filter(years => {
            // creo un oggetto data partendo dal 1 gennaio dell'anno selezionato
            const year = new Date(Number(years.year), 0, 1);
            if (filters.min && filters.max) {
                return year >= filters.min && year < filters.max;
            }
        });
    }

    private applyFiltersAndRecalculate () {
        if (!this.isMonthlyView) {
            this.updateYearsAndView(this.clonedData);
        } else if (this.filteredHistory.length > 0) {
            let history = this.clonedData.filter(el => el.year === this.filteredHistory[0].year);
            this.updateYearsAndView(history);
        } else {
            return;
        }
    }

    private createYearRange (data : YearMonthsData[]) : Months[] | string[] {
        const yearValues = data.map(y => parseInt(y.year, 10));
        this.minValue = Math.min(...yearValues);
        this.maxValue = Math.max(...yearValues) + 1;

        if (data.length === 1 && this.data.length > 1) {
            this.isMonthlyView = true;
            let arrayOfMonths = this.getMonthsArray(data);
            // aggiungo l'anno precedente e l'anno successivo (inizio e fine dello slider)
            arrayOfMonths.unshift({ date: (this.minValue - 1).toString(), description: (this.minValue - 1).toString() });
            arrayOfMonths.push({ date: this.maxValue.toString(), description: this.maxValue.toString() });
            this.maxValueAllowed = this.checkMaxAllowedValueForFiltering(this.data);
            this.minValueAllowed = this.checkMinAllowedValueForFiltering(this.data);
            return arrayOfMonths;
        } else if (data.length === 1 && this.data.length === 1) {
            this.isMonthlyView = true;
            let arrayOfMonths = this.getMonthsArray(data);
            // aggiungo solo l'anno successivo (fine dello slider)
            arrayOfMonths.push({ date: this.maxValue.toString(), description: this.maxValue.toString() });
            return arrayOfMonths;
        }

        const range = [];
        for (let year = this.minValue; year <= this.maxValue; year++) {
            const yearStr = year.toString();
            range.push({ date: yearStr, description: yearStr });
        }
        return range;
    }

    private checkMaxAllowedValueForFiltering (array : YearMonthsData[]): number {
        const lastYear = array[array.length - 1].year;
        return (this.maxValue) > Number(lastYear) ? 13 : 14;
    }

    private checkMinAllowedValueForFiltering (array : YearMonthsData[]) : number {
        const firstYear = array[0].year;
        return this.minValue <= Number(firstYear) ? 1 : 0;
    }

    private onFilterApplied (filters : TimelineFilters) {
        if (this.isMonthlyView) {
            this.filteredHistory = this.filterMonthsView(filters);
        } else {
            this.filteredHistory = this.filterYearsView(filters);
        }
        this.mode === 'clinical' ? this.store.dispatch(seTimelineComponentSetData({ data: this.filteredHistory })) : this.store.dispatch(seTimelineComponentSetReport({ report: this.filteredHistory }));
    }

    private dispatchFilters (data : TimelineFilters) {
        let filters = {
            min: data.min ? data.min.toString() : null,
            max: data.max ? data.max.toString() : null,
            isMonthlyView: data.isMonthlyView
        };
        this.mode === 'clinical' ? this.store.dispatch(seTimelineComponentSetFilters({ filters: filters })) : this.store.dispatch(seTimelineComponentSetReportFilters({ filters: filters }));
    }

    _onStartValueChange (event : any) {
        this.filters.min = new Date(event, 0);
        this.buildFiltersValue();
        this.dispatchFilters(this.filters);
    }

    _onEndValueChange (event : any) {
        this.filters.max = new Date(event, 0);
        this.buildFiltersValue();
        this.dispatchFilters(this.filters);       
    }

    _onFiltersValueChange (filters : any) {
        this.filteredHistory = this.filterMonthsView(filters);
        this.filters = filters;
        this.emitVisitsToShow();
        this.mode === 'clinical' ? this.store.dispatch(seTimelineComponentSetData({ data: this.filteredHistory })) : this.store.dispatch(seTimelineComponentSetReport({ report: this.filteredHistory }));
        let newFilters = cloneDeep(this.filters);
        this.dispatchFilters(newFilters);
    }

    _onStartMonthValueChange (event : any) {
        this.filters.min = this.convertToMonths(event);
        this.buildFiltersValueByMonth();
        this.dispatchFilters(this.filters);
    }

    _onEndMonthValueChange (event : any) {
        this.filters.max = this.convertToMonths(event);
        this.buildFiltersValueByMonth();
        this.dispatchFilters(this.filters);     
    }

    _isOutOfRange (data : any, year : any) {
        if (!this.filters.min || !this.filters.max) {
            return false;
        }
        if (this.isMonthlyView) {
            return (data.date < this.filters.min || data.date >= this.filters.max);
        } else {
            let newMax = this.filters.max.getFullYear() - 1
            return new Date(Number(year.date), 0).getTime() < this.filters.min.getTime() || new Date(Number(year.date), 0).getTime() > new Date(newMax, 0).getTime();
        }
    }
}
